import { createContext, useContext, useState, useEffect } from 'react';
import api, { authApi } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [authError, setAuthError] = useState(null);

  // Check authentication status on mount
  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      try {
        // Decode JWT to get user info and expiration
        const payload = JSON.parse(atob(token.split('.')[1]));
        const currentTime = Date.now() / 1000;
        
        if (payload.exp > currentTime) {
          setIsAuthenticated(true);
          setUser({
            id: payload.sub || payload.nameid,
            email: payload.email,
            firstName: payload.FirstName,
            lastName: payload.LastName,
            name: payload.unique_name,
            role: payload.role || 'User',
            isActive: payload.IsActive === 'True'
          });
          
          // Set token in axios headers
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        } else {
          // Token expired
          localStorage.removeItem('auth_token');
          delete api.defaults.headers.common['Authorization'];
        }
      } catch (error) {
        console.error('Error parsing token:', error);
        localStorage.removeItem('auth_token');
        delete api.defaults.headers.common['Authorization'];
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email, password) => {
    setAuthError(null);
    try {
      const response = await authApi.login(email, password);
      
      if (response.success) {
        const { token } = response;
        localStorage.setItem('auth_token', token);
        
        // Set token in axios headers
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Decode JWT to get user info
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUser({
          id: payload.sub || payload.nameid,
          email: payload.email,
          firstName: payload.FirstName,
          lastName: payload.LastName,
          name: payload.unique_name,
          role: payload.role || 'User',
          isActive: payload.IsActive === 'True'
        });
        
        setIsAuthenticated(true);
        return { success: true };
      }
      
      return { success: false, message: response.message };
    } catch (error) {
      const message = error.response?.data?.message || 'Login failed';
      setAuthError(message);
      return { success: false, message };
    }
  };


  const logout = async () => {
    try {
      await authApi.logout();
    } catch (error) {
      console.error('Logout request failed:', error);
    }
    
    // Clear local state regardless of API call success
    localStorage.removeItem('auth_token');
    delete api.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setUser(null);
    setAuthError(null);
  };

  const isAdmin = () => {
    return user?.role === 'Admin';
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const value = {
    isAuthenticated,
    isLoading,
    user,
    authError,
    login,
    logout,
    isAdmin,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};